// Libs
import classNames from 'classnames/bind';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Grid2, Stack } from '@mui/material';
import { Controller, FormProvider, useForm, UseFormReturn } from 'react-hook-form';
// Components, Layouts, Pages
import { PermissionRole } from '~/components/module/role';
import { BaseCheckbox, BaseInput, BaseToolbar, Loading, UnderDevelopment } from '~/components/common';
// Others
import { RoleEnum } from '~/utils/enum/common';
import { IFormRole } from '~/utils/interface/role';
import { IBodyUpdate } from '~/utils/interface/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { defaultValues, schema } from './helper';
import { compareDataUpdate, removeEmptyObjects } from '~/utils/helper/common';
import { createRole, getDetailRole, updateRole } from '~/thunks/role/roleThunk';
import { BACK_TO_PREVIOUS_PAGE, EMPTY_STRING, ROLE_TYPE_MOBILE } from '~/utils/constants/common';
// Styles, images, icons
import styles from './RoleForm.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const RoleForm = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/role', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id: roleId } = useParams();

  const {
    control,
    reset,
    watch,
    setValue,
    handleSubmit,
    setError,
    formState: { errors, isDirty },
  } = useForm<IFormRole>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValues,
  });

  const { name } = watch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);

  const [initialData, setInitialData] = useState<IFormRole>(defaultValues);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!roleId) return;

    handleGetDetailRole(roleId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleId]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetDetailRole = (roleId: string) => {
    setIsLoading(true);

    dispatch(getDetailRole(roleId))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        const { id, permissions, ...remainingData } = res?.data;

        const dataCodes = permissions?.map((item) => item.code);

        const body: IFormRole = {
          ...remainingData,
          name: remainingData?.name || EMPTY_STRING,
          description: remainingData?.name || EMPTY_STRING,
          codes: dataCodes,
          isMobile: dataCodes?.some((item) => item === ROLE_TYPE_MOBILE),
        };

        setInitialData(body);

        reset(body);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleFormRole = (data: IFormRole) => {
    if (roleId) {
      handleEditRole(data);
    } else {
      handleAddRole(data);
    }

    setIsLoading(true);
  };

  const handleAddRole = (data: IFormRole) => {
    const convertData: IFormRole = {
      ...data,
      codes: [...(data?.codes || []), ...(data?.isMobile ? [ROLE_TYPE_MOBILE] : [])],
    };

    delete convertData.isMobile;

    const cleanData = removeEmptyObjects(convertData) as IFormRole;

    dispatch(createRole(cleanData))
      .unwrap()
      .then((_res) => {
        handleCancel();
      })
      .catch((_err) => {})
      .finally(() => setIsLoading(false));
  };

  const handleEditRole = (data: IFormRole) => {
    const dataChange = compareDataUpdate(initialData, data) as IFormRole;

    const convertData: IFormRole = {
      ...dataChange,
      codes: [...(data?.codes || []), ...(data?.isMobile ? [ROLE_TYPE_MOBILE] : [])],
    };

    delete convertData.isMobile;

    const params: IBodyUpdate<IFormRole> = {
      id: roleId || EMPTY_STRING,
      body: convertData,
    };

    dispatch(updateRole(params))
      .unwrap()
      .then(() => {
        handleCancel();
        setTimeout(() => window.location.reload(), 100);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const handleCancel = () => {
    navigate(BACK_TO_PREVIOUS_PAGE);
    reset();
  };

  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };
  //#endregion Handle Function

  return (
    <Box id='roleFormPage' className={cx('container')}>
      <BaseToolbar title={t(roleId ? 'role_form_title_edit' : 'role_form_title_add')} isBack>
        <Button variant='outlined' className={cx('buttonCancel')} onClick={handleCancel}>
          {t('common_action_cancel', { ns: 'common/base' })}
        </Button>

        <Button variant='contained' onClick={handleSubmit(handleFormRole)} disabled={roleId ? !isDirty : false}>
          {t('common_action_save', { ns: 'common/base' })}
        </Button>
      </BaseToolbar>

      <FormProvider
        {...({
          control,
          reset,
          setValue,
          watch,
          setError,
          formState: { errors, isDirty },
        } as UseFormReturn<IFormRole>)}
      >
        <Stack className={cx('body')}>
          <Grid2 container rowSpacing={3} columnSpacing={5}>
            <Grid2 size={6}>
              <Controller
                control={control}
                name={'name'}
                render={({ field: { onChange, value } }) => {
                  return (
                    <BaseInput
                      height={36}
                      label={t('role_form_title_name')}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors?.name?.message}
                      required
                      disabled={Object.values(RoleEnum).some((item) => item === name)}
                    />
                  );
                }}
              />
            </Grid2>

            <Grid2 size={6}>
              <Stack className={cx('checkMobile')}>
                <Controller
                  control={control}
                  name={'isMobile'}
                  render={({ field: { onChange, value } }) => {
                    return <BaseCheckbox label={t('role_form_title_mobile')} checked={value} onChange={onChange} />;
                  }}
                />
              </Stack>
            </Grid2>

            <Grid2 size={12}>
              <PermissionRole initialCodes={initialData.codes} />
            </Grid2>
          </Grid2>
        </Stack>
      </FormProvider>

      {isLoading && <Loading />}

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}
    </Box>
  );
};

export default RoleForm;
